import { dataFrom } from '@zyno-io/openapi-client-codegen';
import { computed, ref, watch } from 'vue';

import { GlobalState } from '@/global';
import { RmmAccessApi } from '@/openapi-clients-generated/rmm';

import { Logger } from './logger';

const log = Logger.createScoped('RmmAccess');

/**
 * Reactive flags populated from `GET /access` on every tenant switch.
 *
 * - `isMspOrgAccess` — true when the current user reaches the MSP bridge via
 *   the cross-tenant org pivot. Drives launcher tile visibility for org users
 *   and hides management UI that only applies to the primary tenant.
 *
 * - `isRmmManager` — true when the user is an admin OR has the manager flag.
 *   The single flag the SPA should use to decide whether to show management
 *   controls (create tokens, manage technicians, create tags/orgs/scripts, etc.).
 */
export const rmmAccess = ref<{ authorized: boolean; isManager?: boolean; isMsp?: boolean }>({ authorized: false });

const accessKey = computed(() => `${GlobalState.tenant.value?.id}:${GlobalState.user.value?.id}`);
watch(accessKey, refresh, { immediate: true });

async function refresh() {
    const tenant = GlobalState.tenant.value;
    const user = GlobalState.user.value;
    if (!tenant?.id || !user?.id) {
        rmmAccess.value = { authorized: false };
        return;
    }

    const checkKey = accessKey.value;
    try {
        const result = dataFrom(await RmmAccessApi.getAccessCheck());
        // Guard against tenant switch during the in-flight call
        if (checkKey !== accessKey.value) return;
        rmmAccess.value = {
            authorized: true,
            isManager: result.isManager,
            isMsp: result.isMspOrg === false
        };
    } catch (err) {
        log.warn('access check failed', err);
        rmmAccess.value = { authorized: false };
    }
}
